<template>
    <div class="header_costs_analysis is-full-width px-5 py-2">
        <p class="title">Análisis de Costos</p>

        <div v-if="categories.length > 0" class="category_filters is-flex is-align-items-center">
            <SelectComponent
                v-for="cat in categories"
                :key="cat.id"
                class="category_select"
                :options="cat.options"
                :staticLabel="cat.label"
                :initialSelectedValues="selectedItems[cat.id] || []"
                iconRight="ArrowDown"
                theme="classification_theme"
                :isSearchable="true"
                @selected-values="(values) => onCategoryChange(cat.id, values)" />
        </div>

        <div class="right_cluster is-flex is-align-items-center">
            <div v-if="endMonth" class="calendar_picker is-flex is-align-items-center">
                <AnualCalendar
                    :value="endMonth"
                    :disableNewerDates="true"
                    :disabledFrom="getNow"
                    formatLabelDate="MMMM YYYY"
                    :alignRight="true"
                    @date-change="onDateChange" />
            </div>

            <button class="export_btn ml-3 is-flex is-align-items-center is-disabled-local" disabled title="Próximamente">
                <DownloadFile class="mr-2 export_icon" />
                Exportar
            </button>

            <button class="close_btn ml-3" aria-label="Cerrar modal" @click="$emit('close-modal')">
                <Ecs class="ecs" />
            </button>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";
import Ecs from "@/components/svg/Ecs.vue";
import DownloadFile from "@/components/svg/DownloadFile.vue";
import SelectComponent from "@/components/classification/SelectComponent.vue";
import AnualCalendar from "@/components/classification/AnualCalendar.vue";

export default {
    name: "HeaderCostsAnalysis",
    components: { Ecs, DownloadFile, SelectComponent, AnualCalendar },
    props: {
        // Shape esperado por ModalFullHeightRight.headerProps:
        // - periodLabel: String — sólo se usa si endMonth no viene
        // - endMonth: Date — mes focal del drawer; alimenta AnualCalendar
        // - getNow: Date — cota superior (mes actual) para deshabilitar futuro
        // - categories: Array<{ id, label, options: [{value, label}] }> — un
        //   SelectComponent multi-select por categoría dinámica (tiendas/marcas).
        payload: {
            type: Object,
            default: () => ({
                periodLabel: "",
                endMonth: null,
                getNow: null,
                categories: [],
            }),
        },
    },
    computed: {
        // Preselección de los SelectComponent: viene del store costsAnalysis
        // para que el header reaccione a cambios en vivo (destildar un item
        // actualiza el store y el chip se redibuja).
        ...mapState("costsAnalysis", ["selectedItems"]),
        periodLabel() {
            return this.payload?.periodLabel ?? "";
        },
        endMonth() {
            return this.payload?.endMonth ?? null;
        },
        getNow() {
            return this.payload?.getNow ?? null;
        },
        categories() {
            return this.payload?.categories ?? [];
        },
    },
    methods: {
        onDateChange(date) {
            this.$emit("update-end-month", date);
        },
        onCategoryChange(id, values) {
            this.$emit("category-change", { id, values });
        },
    },
};
</script>

<style scoped lang="scss">
// Grid 1fr / auto / 1fr con grid-column explícito por slot — las categorías se
// anclan al centro real del header (no a "después del título") y, si no hay
// categorías, el cluster derecho mantiene su columna sin reflow.
.header_costs_analysis {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 16px;
}

.title {
    grid-column: 1;
    justify-self: start;
    color: var(--report-text);
    font-size: 28px;
    font-weight: 700;
    margin: 0;
}

.category_filters {
    grid-column: 2;
    justify-self: center;
    gap: 8px;
}

.category_select {
    min-width: 110px;
}

.right_cluster {
    grid-column: 3;
    justify-self: end;
}

.calendar_picker {
    border-radius: 8px;
}

.export_btn {
    background: var(--report-bg);
    border: 1px solid var(--report-border);
    border-radius: 8px;
    padding: 6px 14px;
    font-size: 14px;
    font-weight: 500;
    color: var(--report-text-muted-strong);
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
    cursor: pointer;
    transition: background-color 0.15s ease;
    height: 46px;

    &:hover {
        background: var(--report-surface);
    }

    &[disabled] {
        opacity: 0.55;
        cursor: not-allowed;
    }
}

.export_icon {
    width: 16px;
    height: 16px;
}

.close_btn {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: 1px solid var(--report-border-soft);
    background: var(--report-bg);
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
    box-sizing: border-box;
    overflow: hidden;
    padding-top: 5px;
    cursor: pointer;

    .ecs {
        width: 100%;
        position: relative;
        height: 20px;
        overflow: hidden;
        flex-shrink: 0;
    }
}
</style>
